import { useMemo, useState } from 'react'
import './style.scss'
import { Modal, TextField, Button, Text, Flex, Box } from 'src/componentv2'
import { REQUIRED_MSG } from 'src/model/v2/interface/constants'
import { FiCheck } from 'react-icons/fi'
import changePasswordGraphic from 'src/assets/v2/img/change_password_graphic.svg'

const MIN_PASSWORD_LENGTH = 8

export interface ChangePasswordValue {
    oldPassword: string
    newPassword: string
}

export interface ChangePasswordModalProps {
    open: boolean
    onClose: () => void
    onSubmit: (value: ChangePasswordValue) => Promise<boolean> | boolean
}

const EMPTY_FORM = { oldPassword: '', newPassword: '', confirmPassword: '' }

function PasswordRequirements({ password }: { password: string }) {
    const requirements = useMemo(() => [
        { label: 'Minimum of 8 characters', met: password.length >= MIN_PASSWORD_LENGTH },
        { label: 'At least one uppercase letter', met: /[A-Z]/.test(password) },
        { label: 'A symbol or number', met: /[0-9!@#$%^&*()_\-+=[\]{};:'",.<>/?\\|`~]/.test(password) },
    ], [password])

    return (
        <Box className="password-requirements">
            <Text fontSize="1.3" fontWeight="600" color="#344054">
                Password must include
            </Text>
            <Box topMargin="1">
                {requirements.map((req) => (
                    <Flex key={req.label} items="center" gap="0.8" className="password-requirements__item">
                        <span className={`password-requirements__dot ${req.met ? 'password-requirements__dot--met' : ''}`}>
                            {req.met && <FiCheck size={11} strokeWidth={3} />}
                        </span>
                        <Text fontSize="1.4" color={req.met ? '#101828' : '#667085'}>
                            {req.label}
                        </Text>
                    </Flex>
                ))}
            </Box>
        </Box>
    )
}

export const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
                                                                            open, onClose, onSubmit,
                                                                        }) => {
    const [form, setForm] = useState(EMPTY_FORM)
    const [submitAttempted, setSubmitAttempted] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [serverError, setServerError] = useState<string | undefined>()

    const set = (key: keyof typeof form) => (value: string) => {
        setForm((f) => ({ ...f, [key]: value }))
        if (serverError) setServerError(undefined)
    }

    // Validity now mirrors the three checklist rules below, rather than
    // just a length check — a password can't be "valid" if the checklist
    // still shows an unmet item.
    const passwordMeetsRequirements =
        form.newPassword.length >= MIN_PASSWORD_LENGTH &&
        /[A-Z]/.test(form.newPassword) &&
        /[0-9!@#$%^&*()_\-+=[\]{};:'",.<>/?\\|`~]/.test(form.newPassword)

    const errors = {
        oldPassword: !form.oldPassword,
        newPassword: !passwordMeetsRequirements,
        confirmPassword: !form.confirmPassword || form.confirmPassword !== form.newPassword,
    }

    const isFormValid = !errors.oldPassword && !errors.newPassword && !errors.confirmPassword

    const fieldError = (key: keyof typeof errors, message: string = REQUIRED_MSG) =>
        (submitAttempted && errors[key] ? message : undefined)

    const reset = () => {
        setForm(EMPTY_FORM)
        setSubmitAttempted(false)
        setServerError(undefined)
    }

    const handleClose = () => {
        if (isSubmitting) return
        reset()
        onClose()
    }

    const handleSubmit = async () => {
        if (isSubmitting) return

        if (!isFormValid) {
            setSubmitAttempted(true)
            return
        }

        setIsSubmitting(true)
        try {
            const ok = await onSubmit({
                oldPassword: form.oldPassword,
                newPassword: form.newPassword,
            })
            if (ok) {
                reset()
                onClose()
            } else {
                setServerError('Old password is incorrect, please try again.')
            }
        } catch {
            setServerError('Something went wrong, please try again.')
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <Modal open={open} onClose={handleClose} title="Change Password" className="change-password-modal">
            <Flex justify="center">
                <img src={changePasswordGraphic} alt="" className="change-password-modal__graphic" />
            </Flex>

            <Box topMargin="2">
                <TextField
                    label="Current Password"
                    type="password"
                    placeholder="Enter current password"
                    error={fieldError('oldPassword')}
                    value={form.oldPassword}
                    onChange={set('oldPassword')}
                />
            </Box>

            <Box topMargin="1.6">
                <TextField
                    label="New Password"
                    type="password"
                    placeholder="Enter new password"
                    error={submitAttempted && errors.newPassword ? 'Password does not meet requirements' : undefined}
                    value={form.newPassword}
                    onChange={set('newPassword')}
                />
            </Box>

            <Box topMargin="1.6">
                <TextField
                    label="Confirm Password"
                    type="password"
                    placeholder="Confirm new password"
                    error={fieldError('confirmPassword', 'Passwords do not match')}
                    value={form.confirmPassword}
                    onChange={set('confirmPassword')}
                />
            </Box>

            <Box topMargin="1.6">
                <PasswordRequirements password={form.newPassword} />
            </Box>

            {serverError && (
                <Text fontSize="1.3" topMargin="1.2" color="#D92D20">
                    {serverError}
                </Text>
            )}

            <Flex justify="flex-end" topMargin="2.4">
                <Button
                    variant="primary"
                    size="md"
                    disabled={isSubmitting}
                    onClick={handleSubmit}
                >
                    {isSubmitting ? 'Saving...' : 'Save changes'}
                </Button>
            </Flex>
        </Modal>
    )
}

export default ChangePasswordModal